<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

  <!-- Navbar -->
  <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
    <HrTopbar
      :page-title="currentPageTitle"
      :is-sidebar-open="isSidebarOpen"
      :is-dark-mode="isDarkMode"
      @toggle-sidebar="toggleSidebar"
      @toggle-dark-mode="toggleDarkMode"
    />
  </header>

  <!-- Page Content -->
  <div class="p-6 mt-16">

    <!-- Top Bar -->
    <div class="flex justify-between items-center mb-8 p-4">

      <input
        v-model="search"
        type="text"
        placeholder="Search allowance..."
        class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72  focus:outline-none focus:ring-2 focus:ring-secondary dark:bg-gray-700 dark:text-white"
      />

      <button
        @click="openCreate"
        class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
      >
        + Add Allowance
      </button>

    </div>

    <!-- Department Grid -->
    <div
      v-if="filteredBenefits.length"
      class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >

      <div
        v-for="ben in filteredBenefits"
        :key="ben.id"
        class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition group"
      >

        <!-- Card Header -->
        <div class="flex justify-between items-start mb-4">

          <div class="flex items-center space-x-3">

            <div class="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-white font-bold text-lg">
              {{ ben.code }}
            </div>

            <div>
              <h3 class="font-semibold text-gray-800 dark:text-white">
                {{ ben.name }}
              </h3>

              <!-- <span class="text-xs text-gray-500">
                {{ dept.staff }} staff
              </span> -->
            </div>

          </div>

        </div>

        <!-- Description -->
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {{ ben.description }}
        </p>

        <!-- Actions -->
        <div class="flex justify-between items-center text-sm">

          <button
            @click="editBenefit(ben)"
            class="text-blue-600 hover:underline"
          >
            <!-- <i class="fas fa-edit mr-2"></i> -->
            Edit
          </button>

          <button
            @click="deleteBenefit(ben.id)"
            class="text-red-500 hover:underline"
          >
            <!-- <i class="fas fa-trash mr-2"></i> -->
            Delete
          </button>

        </div>

      </div>

    </div>

    <!-- Empty State -->
    <div
      v-else
      class="text-center text-gray-400 mt-20"
    >
      No Benefits yet
    </div>


    <!-- Modal -->
    <Transition name="modal">
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >

      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">

        <h3 class="text-lg font-semibold text-[#035a76] mb-6">
          {{ editing ? 'Edit Benefit' : 'Create Benefit' }}
        </h3>

        <form @submit.prevent="saveBenefit" class="space-y-4">

          <input
            v-model="form.name"
            type="text"
            placeholder="Benefit Name"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <input
            v-model="form.code"
            type="text"
            placeholder="Code (HR, FIN, IT...)"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <!-- <input
            v-model="form.staff"
            type="number"
            placeholder="Staff count"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          /> -->

          <textarea
            v-model="form.description"
            placeholder="Description"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          ></textarea>

          <div class="flex justify-end space-x-3 pt-2">

            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
            >
              Save
            </button>

          </div>

        </form>

      </div>

    </div>
    </Transition>

  </div>

  </main>

</div>
</template>

<script setup>

import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Departments")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

const benefits = ref([])

// const departments = ref([
// {
// id:1,
// name:"Human Resources",
// code:"HR",
// description:"Handles recruitment and employee relations",
// staff:12
// },
// {
// id:2,
// name:"Finance",
// code:"FIN",
// description:"Company financial management",
// staff:8
// },
// {
// id:3,
// name:"Information Technology",
// code:"IT",
// description:"System infrastructure and support",
// staff:6
// }
// ])

// const form = ref({
// id:null,
// name:"",
// code:"",
// description:"",
// staff:0
// })

const fetchBenefits = async () => {
  try{
    const response = await api.get("Benefits.php");
    benefits.value = response.data.map(benefit => ({
      id:benefit.id,
      name:benefit.name,
      code:benefit.code,
      description:benefit.description
    }));

  } catch (error) {
    console.error("Error fetching Benefits:", error);
  }
};

const form = ref({ id:null, name:"", code:"", description:""});


const filteredBenefits = computed(() => {
return benefits.value.filter(d =>
d.name.toLowerCase().includes(search.value.toLowerCase())
)
})

const openCreate = () => {
editing.value = false
form.value = {id:null,name:"",code:"",description:"",staff:0}
showModal.value = true
}

const editBenefit = (ben) => {
editing.value = true
form.value = {...dept}
showModal.value = true
}

// const saveDepartment = () => {

// if(editing.value){

// const index = departments.value.findIndex(d => d.id === form.value.id)
// departments.value[index] = {...form.value}

// }else{

// departments.value.push({
// id:Date.now(),
// ...form.value
// })

// }

// closeModal()

// }

const saveBenefit = async () =>{
  try{
  let response;

  if(editing.value){
    response = await api.put("Benefits.php", form.value)
    const index = departments.value.findIndex(d => d.id === form.value.id)
      if (index !== -1) {
        benefits.value[index] = { ...form.value };
      }
      
  }else{
        response = await api.post("Benefits.php", form.value)
        benefits.value.push({
          id:response.data.id,
          name:response.data.name,
          code:response.data.code,
          description:response.data.description
        });
      }
closeModal();

  } catch (error) {
    console.error("Error saving Benefit:", error);
  }
};


const deleteBenefit = async (id) => {
try {

    await api.delete("Benefits.php", {
      data: { id: id }
    });

    benefits.value = benefits.value.filter(b => b.id !== id);

  } catch (error) {
    console.error("Error deleting benefit:", error);
  }
}

const closeModal = () => {
showModal.value = false
}

const toggleSidebar = () => {
isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {

isDarkMode.value = !isDarkMode.value

if(isDarkMode.value){
document.documentElement.classList.add("dark")
}else{
document.documentElement.classList.remove("dark")
}

}
onMounted(() =>{
  fetchBenefits();
});

</script>

<style>

.modal-enter-active,
.modal-leave-active{
transition: all .25s ease;
}

.modal-enter-from{
opacity:0;
transform:scale(.9);
}

.modal-leave-to{
opacity:0;
transform:scale(.9);
}

</style>